/**
 * Get Right PC — Blog Article FAQ accordion
 *
 * Context: FAQ questions/answers are NOT a separate section or snippet — they are
 * plain rich-text typed straight into each article's body in Shopify admin, always
 * as a "FAQ" heading followed by paragraphs shaped like:
 *   <p><strong>Question text?</strong><br>Answer text.</p>
 *
 * This script finds that pattern inside the article body (.article-grpc__content)
 * and rebuilds it, in the browser only, into a click-to-open accordion with a
 * rotating chevron. It never touches the stored article content in Shopify — if
 * this script is ever removed, the article still renders correctly as plain text.
 *
 * Scope: runs only inside .article-grpc__content on the Blog Article template.
 * Guarded by the "Turn FAQ sections into a click-to-open accordion" theme setting
 * (data-grpc-faq-enabled attribute) so it can be switched off with no code changes.
 */
(function () {
  'use strict';

  var CHEVRON_SVG =
    '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.75" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function isFaqHeading(el) {
    if (!el || !/^H[1-3]$/.test(el.tagName)) return false;
    var text = (el.textContent || '').trim().toLowerCase();
    return text === 'faq' || text === 'faqs' || text === 'frequently asked questions';
  }

  function splitQuestionAnswer(p) {
    var strong = p.querySelector('strong');
    if (!strong) return null;

    // Question = the <strong> text. Answer = whatever HTML comes after the
    // first <br> that follows the <strong> (falls back to text after the
    // strong tag if there's no <br>).
    var clone = p.cloneNode(true);
    var cloneStrong = clone.querySelector('strong');
    if (!cloneStrong) return null;

    var answerNodes = [];
    var node = cloneStrong.nextSibling;
    var skippedBr = false;
    while (node) {
      var next = node.nextSibling;
      if (!skippedBr && node.nodeType === 1 && node.tagName === 'BR') {
        skippedBr = true;
        node.parentNode.removeChild(node);
        node = next;
        continue;
      }
      answerNodes.push(node);
      node = next;
    }

    var answerHtml = answerNodes
      .map(function (n) {
        return n.nodeType === 1 ? n.outerHTML : n.textContent;
      })
      .join('')
      .trim();

    if (!answerHtml) return null;

    return {
      question: strong.textContent.trim(),
      answerHtml: answerHtml
    };
  }

  function buildAccordion(headingEl, pairs) {
    var wrap = document.createElement('div');
    wrap.className = 'grpc-faq';

    var heading = document.createElement(headingEl.tagName.toLowerCase());
    heading.className = 'grpc-faq__heading';
    heading.textContent = headingEl.textContent.trim();
    wrap.appendChild(heading);

    var list = document.createElement('div');
    list.className = 'grpc-faq__list';
    wrap.appendChild(list);

    pairs.forEach(function (pair, index) {
      var item = document.createElement('div');
      item.className = 'grpc-faq__item';

      var uid = 'grpc-faq-' + Math.random().toString(36).slice(2, 9) + '-' + index;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'grpc-faq__question';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', uid);

      var label = document.createElement('span');
      label.textContent = pair.question;
      button.appendChild(label);

      var chevron = document.createElement('span');
      chevron.className = 'grpc-faq__chevron';
      chevron.innerHTML = CHEVRON_SVG;
      button.appendChild(chevron);

      var answerWrap = document.createElement('div');
      answerWrap.className = 'grpc-faq__answer-wrap';
      answerWrap.id = uid;

      var answer = document.createElement('div');
      answer.className = 'grpc-faq__answer';

      var answerInner = document.createElement('div');
      answerInner.className = 'grpc-faq__answer-inner';
      answerInner.innerHTML = pair.answerHtml;

      answer.appendChild(answerInner);
      answerWrap.appendChild(answer);

      button.addEventListener('click', function () {
        var isOpen = item.classList.toggle('grpc-faq__item--open');
        button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });

      item.appendChild(button);
      item.appendChild(answerWrap);
      list.appendChild(item);
    });

    return wrap;
  }

  function enhance(container) {
    if (!container || container.getAttribute('data-grpc-faq-enabled') !== 'true') return;
    if (container.getAttribute('data-grpc-faq-processed') === 'true') return;

    var headings = container.querySelectorAll('h1, h2, h3');
    var faqHeading = null;
    for (var i = 0; i < headings.length; i++) {
      if (isFaqHeading(headings[i])) {
        faqHeading = headings[i];
        break;
      }
    }
    if (!faqHeading) return;

    // Collect sibling <p> elements after the FAQ heading, stopping at the
    // next heading (or a non-p, non-question element that isn't part of it).
    var pairs = [];
    var toRemove = [faqHeading];
    var sibling = faqHeading.nextElementSibling;
    while (sibling && !/^H[1-3]$/.test(sibling.tagName)) {
      if (sibling.tagName === 'P' && sibling.querySelector('strong')) {
        var pair = splitQuestionAnswer(sibling);
        if (pair) {
          pairs.push(pair);
          toRemove.push(sibling);
          sibling = sibling.nextElementSibling;
          continue;
        }
      }
      // Any other element right after the FAQ block (e.g. a "Related:" line,
      // byline, or script tag) ends the FAQ run — leave it exactly as is.
      break;
    }

    if (pairs.length === 0) return;

    var accordion = buildAccordion(faqHeading, pairs);
    faqHeading.parentNode.insertBefore(accordion, faqHeading);
    toRemove.forEach(function (el) {
      el.parentNode.removeChild(el);
    });

    container.setAttribute('data-grpc-faq-processed', 'true');
  }

  function init() {
    var containers = document.querySelectorAll('.article-grpc__content[data-grpc-faq-enabled="true"]');
    containers.forEach ? containers.forEach(enhance) : Array.prototype.forEach.call(containers, enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run inside the Shopify theme editor when this section is re-rendered
  // after a settings change, so the accordion isn't lost while editing.
  document.addEventListener('shopify:section:load', function (event) {
    var container = event.target.querySelector
      ? event.target.querySelector('.article-grpc__content')
      : null;
    if (container) enhance(container);
  });
})();